// Wrapper agnóstico sobre PostHog. Toda la app reporta errores y breadcrumbs
// por acá; si en algún momento cambiamos a Sentry, solo se toca este archivo.
//
// El cliente ya tiene `capture_exceptions: true` en instrumentation-client.ts
// — eso atrapa excepciones no manejadas + unhandledRejection automáticamente.
// Estas funciones son para captures EXPLÍCITOS desde el wrapper de server
// actions / API handlers / boundaries.

import { isAppError, type AppError, type ErrorCode } from "@/lib/api/errors";
import { scrubPII } from "@/lib/observability/pii-scrub";

// PostHog distinto en server vs cliente — abstraemos.
const isServer = typeof window === "undefined";

// Códigos que se reportan como `level: "warning"` en vez de `"error"` — para
// que en PostHog se puedan filtrar como ruido conocido. NO se ignoran (el
// usuario pidió "absolutamente cualquier error"), solo se etiquetan distinto.
const WARNING_CODES: ReadonlySet<ErrorCode> = new Set([
  "validation",
  "not_found",
  "unauthorized",
]);

export interface ErrorContext {
  // Quién lo dispara — userId interno, NUNCA email/nombre.
  userId?: string | null;
  // De dónde viene — nombre de la action, route, componente.
  source?: string;
  // Tags adicionales (agencyId, role, casefileId, etc — ids internos, no PII).
  tags?: Record<string, string | number | boolean | null | undefined>;
  // Extra context — se sanitiza con scrubPII antes de mandar.
  extra?: Record<string, unknown>;
}

function severityFor(err: unknown): "error" | "warning" {
  if (isAppError(err) && WARNING_CODES.has(err.code)) return "warning";
  return "error";
}

function buildProperties(err: unknown, ctx?: ErrorContext) {
  const props: Record<string, unknown> = {
    $exception_level: severityFor(err),
    source: ctx?.source ?? "unknown",
  };

  if (isAppError(err)) {
    props.error_code = err.code;
    if (err.meta) props.error_meta = scrubPII(err.meta);
    if (err.fieldErrors) props.field_errors = Object.keys(err.fieldErrors);
  }

  if (ctx?.tags) {
    for (const [k, v] of Object.entries(ctx.tags)) {
      if (v !== undefined && v !== null) props[`tag_${k}`] = v;
    }
  }

  if (ctx?.extra) {
    props.context = scrubPII(ctx.extra);
  }

  return props;
}

// =====================================================================
// captureError — uso principal
// =====================================================================
//
// Llamado por el wrapper de server actions / API handlers cuando un error
// llega al catch top-level. También por `error.tsx` boundaries en cliente.

export async function captureError(
  err: unknown,
  ctx?: ErrorContext,
): Promise<void> {
  const properties = buildProperties(err, ctx);
  const distinctId = ctx?.userId ?? "anonymous";

  try {
    if (isServer) {
      const { getPostHogClient } = await import("@/lib/observability/posthog-server");
      const ph = getPostHogClient();
      ph.captureException(err, distinctId, properties);
      // flushAt:1 + flushInterval:0 → ya manda inmediatamente, pero shutdown
      // garantiza que termine en serverless antes de que el worker muera.
      await ph._shutdown();
    } else {
      const { default: posthog } = await import("posthog-js");
      // posthog-js tiene captureException nativo desde 1.180+.
      posthog.captureException(err, properties);
    }
  } catch (reportErr) {
    // Nunca dejamos que el reporte rompa la app. Lo único que podemos hacer
    // es loguear localmente para que aparezca en el log del runtime.
    console.error("[observability] failed to report error", reportErr);
  }
}

// =====================================================================
// addBreadcrumb — eventos de contexto que ayudan a debuggear
// =====================================================================
//
// No es un error — es un evento que queda en el historial del usuario en
// PostHog para que cuando haya un error, sepamos qué hizo antes. Ej:
// "case.status_changed", "dispatch.created", "login.success".

export async function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): Promise<void> {
  const safeData = data ? scrubPII(data) : undefined;
  try {
    if (isServer) {
      // En server, los breadcrumbs son eventos sueltos — PostHog server-side
      // los registra como events y se asocian al distinct_id si se pasa.
      // Si no hay user en contexto, se pierde — está OK para v1.
      // (En cliente sí van asociados a la session automáticamente.)
      return;
    }
    const { default: posthog } = await import("posthog-js");
    posthog.capture(`breadcrumb:${category}`, {
      message,
      ...(safeData ?? {}),
    });
  } catch {
    // silent
  }
}

// =====================================================================
// setUserContext — identifica al user en PostHog
// =====================================================================
//
// Se llama una vez por sesión desde AuthProvider, apenas la session pasa a
// "authenticated". Manda dos cosas:
//
// 1. `identify(id, props)` — atás el person interno (uuid) con propiedades
//    que PostHog usa para mostrar en Activity → Person (name, email) y para
//    filtrar (role, tenant, agency).
// 2. `register(superProps)` — super properties que se adjuntan a CADA evento
//    posterior (incluidos session recordings, autocapture, breadcrumbs). Esto
//    es lo que te permite filtrar toda la actividad por tenant/agency/role
//    sin tener que cruzar con persons.

export interface UserContext {
  id: string;
  name: string;
  email: string | null;
  role: string;
  tenant: string;
  agency: string;
  shift?: string;
}

export function setUserContext(user: UserContext): void {
  if (isServer) return; // server identifica por captura, no por estado global
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.identify(user.id, {
      name: user.name,
      email: user.email ?? undefined,
      role: user.role,
      tenant: user.tenant,
      agency: user.agency,
    });
    posthog.register({
      tenant: user.tenant,
      agency: user.agency,
      role: user.role,
      ...(user.shift ? { shift: user.shift } : {}),
    });
  });
}

// =====================================================================
// reset — al logout
// =====================================================================

export function resetUserContext(): void {
  if (isServer) return;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.reset();
  });
}

// Re-export para los call sites que vienen.
export type { ErrorCode, AppError };
